import styles from "./TestbuyDetailSheet.module.css";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { TestbuySummaryInfo } from "../TestbuySummaryInfo/TestbuySummaryInfo";
import { useGetTestbuy } from "../../hooks/useTestbuyV2";
import LoadingTaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/components/LoadingTaskCard/LoadingTaskCard";
import {
  FixaAccordion,
  FixaAccordionItem,
} from "@coworker/apprestructured/src/shared/wrappers/FixaAccordion/FixaAccordion";
import { useFormatFieldValue } from "@coworker/apprestructured/src/task-management/hooks/useFormatFieldValue";
import { ItemCardWithLocation } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCardWithLocation/ItemCardWithLocation";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { GetNewButton } from "../GetNewButton/GetNewButton";
import { ChangeWithButton } from "../ChangeWithButton/ChangeWithButton";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import CheckmarkCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CheckmarkCircleIconPath";
import CrossCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CrossCircleIconPath";
import WarningTrianglePath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/WarningTrianglePath";
import { Testbuy } from "@coworker/apprestructured/src/testbuy/services/testbuy.service";
import CountedProgress from "@coworker/apprestructured/src/task-management/components/CountedProgress/CountedProgress";
import { useCallback } from "react";
import { TestBuyProductWithCriteria } from "@coworker/types/lib/testbuy";
import { useTask } from "@coworker/apprestructured/src/task-management/services/useTask";
import ChevronRightIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChevronRightIconPath";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";

interface TestbuyDetailSheetProps {
  visible: boolean;
  testbuyId: string;
  onClose: () => void;
}

const mapArticleToItem = (article: TestBuyProductWithCriteria): Item => {
  return {
    no: article.id || "",
    name: article.name,
    noFormatted: article.shortId,
    description: article.description,
    source: article.location,
    smallImageUrl: article.smallImage,
    imageUrl: article.smallImage,
    salesPrice: {
      price: article.price,
      currency: article.currency,
    },
    // The following fields are not available in the testbuy article response. But they're not used anyway
    hfbNo: "",
    type: "",
    productTypeName: "",
    paNo: "",
    outgoing: false,
  };
};

const ArticleDetail = ({
  article,
  testbuy,
  hfb,
}: {
  article: TestBuyProductWithCriteria;
  testbuy: Testbuy;
  hfb: string;
}) => {
  const item = mapArticleToItem(article);
  const allCriteriaAnswered = article.criteria.every(
    (c) => c.state !== "NOT_ANSWERED"
  );
  const [, setSearchParams] = useSearchParamState();

  const handleSelectArticle = useCallback(
    (articleId: string) => {
      setSearchParams((params) => {
        params.set("testbuyArticleId", articleId);
      });
    },
    [setSearchParams]
  );

  if (!article || !article.id) return null;

  return (
    <div>
      <FixaListViewItem
        key={article.id}
        id={article.id}
        className={styles["article-item"]}
        title={
          <div className={styles["article-item-detail"]}>
            <div role="button" onClick={() => handleSelectArticle(article.id!)}>
              <ItemCardWithLocation
                location={article.location}
                item={item}
                extra={
                  allCriteriaAnswered ? (
                    <ArticleStatusMessage article={article} />
                  ) : (
                    <div
                      className={styles["article-actions"]}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <GetNewButton
                        testbuy={testbuy}
                        article={article}
                        area={hfb}
                      />
                      <ChangeWithButton
                        testbuy={testbuy}
                        article={article}
                        area={hfb}
                      />
                    </div>
                  )
                }
              />
            </div>

            <FixaButton
              onClick={() => handleSelectArticle(article.id!)}
              type="tertiary"
              size="small"
              ssrIcon={ChevronRightIconPath}
              iconOnly
            />
          </div>
        }
        inset
      />
    </div>
  );
};

const StatusBadge = ({
  variant,
  children,
}: {
  variant: "passed" | "failed" | "warning";
  children: React.ReactNode;
}) => {
  const iconPath =
    variant === "passed"
      ? CheckmarkCircleIconPath
      : variant === "warning"
        ? WarningTrianglePath
        : CrossCircleIconPath;

  return (
    <div className={styles["article-status"]}>
      <FixaSSRIcon paths={iconPath} className={styles[variant]} />
      <span className={styles[variant]}>{children}</span>
    </div>
  );
};

const ArticleStatusMessage = ({
  article,
}: {
  article: TestBuyProductWithCriteria;
}) => {
  // hooks
  const { t } = useTranslation();

  // data
  const failedCriteria = article.criteria.filter((c) => c.state === "NOT_OK");
  const totalCriteria = article.criteria.length;

  // failed-criteria
  const hasFailedCriteria = failedCriteria.length > 0;
  if (hasFailedCriteria && article.followUpTaskId) {
    return (
      <StatusBadge variant="failed">
        {t("failedCountString", {
          count: `${failedCriteria.length}/${totalCriteria}`,
        })}
      </StatusBadge>
    );
  }

  // follow-up task not created yet
  const needsFollowUpTask = hasFailedCriteria && !article.followUpTaskId;
  if (needsFollowUpTask) {
    return (
      <StatusBadge variant="warning">
        {t("createFollowUpTaskString")}
      </StatusBadge>
    );
  }

  // failed but fixed immediately
  const allFilled = article.criteria.every((c) => c.state !== "NOT_ANSWERED");
  const someFixed = article.criteria.some((c) => c.state === "NOT_OK_FIXED");
  if (allFilled && someFixed) {
    return <StatusBadge variant="warning">{t("tbNotOkayFixed")} </StatusBadge>;
  }

  // all criteria passed
  const allPassed = article.criteria.every(
    (c) => c.state === "OK" || c.state === "NOT_OK_FIXED"
  );
  if (allPassed) {
    return <StatusBadge variant="passed">{t("passedString")}</StatusBadge>;
  }

  return null;
};

const InsufficientArticlesMessage = ({
  hfb,
  articleCount,
}: {
  hfb: string;
  articleCount: number;
}) => {
  // hooks
  const { t } = useTranslation();
  const formatFieldValue = useFormatFieldValue();

  // data
  const formattedHfb = formatFieldValue(hfb, "location-filter");

  if (articleCount === 0) {
    return (
      <FixaInlineMessage
        className={styles["insufficient-articles-message"]}
        variant="negative"
        title={t("noArticlesAvailableString")}
        body={t("noArticlesAvailableForHfbString", {
          hfb: formattedHfb,
        })}
      />
    );
  }

  return (
    <FixaInlineMessage
      className={styles["insufficient-articles-message"]}
      variant="cautionary"
      title={t("articlesAvailableString", { count: articleCount })}
      body={t("articlesAvailableForHfbString", {
        count: articleCount,
        hfb: formattedHfb,
      })}
    />
  );
};

const HfbTestbuyDetail = ({
  hfb,
  articles,
  expectedPerHfb,
  testbuy,
}: {
  hfb: string;
  articles: TestBuyProductWithCriteria[];
  expectedPerHfb: number;
  testbuy: Testbuy;
}) => {
  const hasInsufficientArticles = articles.length < expectedPerHfb;
  return (
    <>
      {hasInsufficientArticles && (
        <InsufficientArticlesMessage hfb={hfb} articleCount={articles.length} />
      )}

      <FixaListView id={`articles-${hfb}`}>
        {articles.map((article) => (
          <ArticleDetail
            key={article.id}
            article={article}
            testbuy={testbuy}
            hfb={hfb}
          />
        ))}
      </FixaListView>
    </>
  );
};

const ArticlesByHfb = ({ testbuy }: { testbuy: Testbuy }) => {
  // hooks
  const formatFieldValue = useFormatFieldValue();
  const { data: task } = useTask(testbuy.taskId!);

  // data
  const { articles, areas, strategy } = testbuy;

  // Get the expected articles per HFB from the associated task
  const expectedPerHfb = task?.details?.["articles_count"] as number;

  // Group articles by area once
  const articlesPerHfb = articles.reduce<
    Record<string, TestBuyProductWithCriteria[]>
  >((acc, article) => {
    const area = article.area;
    if (!area) return acc;
    if (!acc[area]) acc[area] = [];
    acc[area].push(article);
    return acc;
  }, {});

  if (!areas.length) {
    return null;
  }

  // Single HFB
  const firstHfb = areas[0];
  if (areas.length === 1 || strategy === "CUSTOM") {
    if (!firstHfb) return null; // This should never happen

    return (
      <HfbTestbuyDetail
        hfb={firstHfb}
        articles={articles}
        expectedPerHfb={expectedPerHfb}
        testbuy={testbuy}
      />
    );
  }

  // Multiple HFBs
  return (
    <FixaAccordion size="medium" padded>
      {areas.map((hfb) => (
        <FixaAccordionItem
          key={hfb}
          id={`hfb-${hfb}`}
          title={formatFieldValue(hfb, "location-filter")}
          open={true}
        >
          <HfbTestbuyDetail
            hfb={hfb}
            articles={articlesPerHfb[hfb] ?? []}
            expectedPerHfb={expectedPerHfb}
            testbuy={testbuy}
          />
        </FixaAccordionItem>
      ))}
    </FixaAccordion>
  );
};

export const TestbuyDetailSheet = ({
  visible,
  testbuyId,
  onClose,
}: TestbuyDetailSheetProps) => {
  const { t } = useTranslation();

  const { data: testbuy, isLoading } = useGetTestbuy(
    visible ? testbuyId : null
  );

  const totalArticles = testbuy?.numberOfArticles ?? 0;
  const completedArticles =
    testbuy?.articles.filter((a) =>
      a.criteria.every((c) => c.state !== "NOT_ANSWERED")
    ).length ?? 0;

  if (isLoading) {
    return <LoadingTaskCard />;
  }

  if (!testbuy) {
    return null; // This should never happen. You'd be redirected
  }

  const summaryData = {
    articlesCount: testbuy.numberOfArticles,
    areas: testbuy.areas || [],
    strategy: testbuy.strategy,
    includeEds: testbuy.includeOutgoingArticles,
    includeOutOfStock: testbuy.includeOutOfStockArticles,
  };

  return (
    <FixaModal
      visible={visible}
      handleCloseBtn={onClose}
      className={styles["modal"]}
    >
      <FixaSheets
        labelledById="testbuy-sheet-header"
        header={
          <FixaModalHeader
            id="testbuy-sheet-header"
            title={t("testBuyTaskString")}
            closeBtnClick={onClose}
          />
        }
        footer={null}
        fullSize
      >
        <div className={styles["sheet-content"]}>
          <div className={styles["summary-container"]}>
            <CountedProgress
              max={totalArticles}
              count={completedArticles}
              label={t("articlesTestedString")}
              colorByCompletion
              type="light"
            />

            <div className={styles["grey-card"]}>
              <TestbuySummaryInfo {...summaryData} />
            </div>
          </div>

          <ArticlesByHfb testbuy={testbuy} />
        </div>
      </FixaSheets>
    </FixaModal>
  );
};
